<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import DealsAPI from 'dashboard/api/deals';
/* global axios */

const store = useStore();
// Sucursal activa del switcher (donde está conectado el usuario)
const activeBranchId = computed(
  () => store.getters['branches/getActiveBranchId'] || null
);

const props = defineProps({
  pipeline: { type: Object, required: true },
  defaultStageId: { type: Number, default: null },
  accountId: { type: [String, Number], required: true },
});

const emit = defineEmits(['close', 'created']);

// --- Campo único inteligente ---
const query = ref('');
const searchResults = ref([]);
const searching = ref(false);
const showResults = ref(false);
const selectedContact = ref(null); // contacto existente elegido
const creatingNew = ref(false); // creando contacto nuevo
const dupContact = ref(null); // duplicado detectado → ofrecer escribirle directo

// Normaliza teléfono Rep. Dom.: si son 10 dígitos (809/849/829) antepone el
// código país 1 → así el usuario solo escribe los 10 dígitos (Victor 2026-05-22).
function normalizeDoDigits(raw) {
  let d = String(raw || '').replace(/[^0-9]/g, '');
  if (d.length === 10 && /^(809|849|829)/.test(d)) d = `1${d}`;
  return d;
}

// --- Nuevo contacto ---
const channel = ref('whatsapp'); // 'whatsapp' | 'instagram'
const newName = ref('');
const newIdentifier = ref(''); // teléfono (WA) o @usuario (IG)
const inboxId = ref(null);
const inboxes = ref([]);
const showInboxPicker = ref(false);

// --- Detalles opcionales (plegados) ---
const showMore = ref(false);
const title = ref('');
const value = ref(0);
const currency = ref('DOP');
const stageId = ref(props.defaultStageId || props.pipeline.stages?.[0]?.id || null);

const saving = ref(false);
const errorMsg = ref(null);

let searchTimer = null;

onMounted(async () => {
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${props.accountId}/inboxes`
    );
    inboxes.value = data.payload || [];
  } catch {
    inboxes.value = [];
  }
});

// Sucursales del embudo (scope_branch_ids). Vacío = embudo global → todas.
const pipelineBranchIds = computed(() => {
  const ids = props.pipeline?.scope_branch_ids;
  return Array.isArray(ids) ? ids : [];
});

// Ordena las bandejas por capacidad real de envío para que el default ([0])
// sea la WAHA CONECTADA (con la que sí se le puede escribir) y nunca un bridge
// muerto. No hardcodea ids: cada sucursal usa su propia WAHA conectada.
function deliverabilityRank(i) {
  const wh = String(i.webhook_url || '').toLowerCase();
  const connected = i.additional_attributes?.conn_status === 'connected';
  const isWaha = wh.includes('waha');
  if (isWaha && connected) return 0;
  if (connected) return 1;
  if (isWaha) return 2;
  return 3;
}
function byDeliverability(list) {
  return [...list].sort((a, b) => deliverabilityRank(a) - deliverabilityRank(b));
}

const channelInboxes = computed(() => {
  const wa = ['Channel::Api', 'Channel::Whatsapp'];
  const ofChannel = inboxes.value.filter(i =>
    channel.value === 'whatsapp'
      ? wa.includes(i.channel_type)
      : i.channel_type === 'Channel::Instagram'
  );
  if (activeBranchId.value) {
    const inActive = ofChannel.filter(i => i.branch_id === activeBranchId.value);
    if (inActive.length) return byDeliverability(inActive);
  }
  const branchIds = pipelineBranchIds.value;
  if (branchIds.length) {
    const inScope = ofChannel.filter(i => branchIds.includes(i.branch_id));
    if (inScope.length) return byDeliverability(inScope);
  }
  return byDeliverability(ofChannel);
});

const selectedInbox = computed(
  () => channelInboxes.value.find(i => i.id === inboxId.value) || null
);

const currentStageName = computed(
  () => props.pipeline.stages?.find(s => s.id === stageId.value)?.name || ''
);

// Mantiene inboxId SIEMPRE válido respecto a las bandejas disponibles. Sin
// esto había una carrera: las bandejas cargan antes que la sucursal activa →
// inboxId quedaba apuntando a una bandeja de otra sucursal que luego desaparece
// de la lista → "sin bandeja" y botón bloqueado (Victor 2026-05-21).
watch(
  channelInboxes,
  list => {
    if (!list.find(i => i.id === inboxId.value)) {
      inboxId.value = list[0]?.id || null;
    }
  },
  { immediate: true }
);

watch(query, q => {
  clearTimeout(searchTimer);
  const s = (q || '').trim();
  if (s.length < 2) {
    searchResults.value = [];
    showResults.value = false;
    return;
  }
  showResults.value = true;
  searchTimer = setTimeout(async () => {
    searching.value = true;
    try {
      const { data } = await axios.get(
        `/api/v1/accounts/${props.accountId}/contacts/search?q=${encodeURIComponent(s)}&include=contact_inboxes`
      );
      searchResults.value = (data.payload || []).slice(0, 8);
    } catch {
      searchResults.value = [];
    } finally {
      searching.value = false;
    }
  }, 250);
});

function pickContact(contact) {
  selectedContact.value = contact;
  creatingNew.value = false;
  showResults.value = false;
  if (!title.value) title.value = contact.name || '';
}

// Pasa a "crear nuevo": si lo tecleado son dígitos lo usa como teléfono, si es
// texto lo usa como nombre. La bandeja se autoselecciona.
function startCreateNew() {
  const raw = query.value.trim();
  const digits = raw.replace(/[^0-9]/g, '');
  if (digits.length >= 7 && channel.value === 'whatsapp') {
    newIdentifier.value = raw;
    newName.value = '';
  } else {
    newName.value = raw;
    newIdentifier.value = '';
  }
  if (!inboxId.value) inboxId.value = channelInboxes.value[0]?.id || null;
  creatingNew.value = true;
  selectedContact.value = null;
  showResults.value = false;
}

function reset() {
  selectedContact.value = null;
  creatingNew.value = false;
  dupContact.value = null;
  errorMsg.value = null;
  query.value = '';
  newName.value = '';
  newIdentifier.value = '';
  searchResults.value = [];
  showResults.value = false;
}

// El número ya existía → usar ese contacto y abrirle el chat directo (un clic).
async function writeToExisting() {
  if (!dupContact.value || saving.value) return;
  selectedContact.value = dupContact.value;
  creatingNew.value = false;
  dupContact.value = null;
  errorMsg.value = null;
  await save(true);
}

// Si editas nombre/teléfono después del aviso de duplicado, limpia el banner.
watch([newName, newIdentifier], () => {
  if (dupContact.value) {
    dupContact.value = null;
    errorMsg.value = null;
  }
});

const canSave = computed(() => {
  if (!stageId.value) return false;
  if (selectedContact.value) return true;
  if (creatingNew.value) {
    return (
      !!newName.value.trim() && !!newIdentifier.value.trim() && !!inboxId.value
    );
  }
  return false;
});

// Busca el contacto que choca con lo tecleado (mismo nº de WA por últimos 10
// dígitos, o mismo @usuario IG). Devuelve el contacto o null. Sirve tanto para
// el pre-chequeo como para el fallback cuando el backend rechaza por duplicado.
async function locateDuplicate() {
  const raw = newIdentifier.value.trim();
  if (!raw) return null;
  const last10 = s => String(s || '').replace(/[^0-9]/g, '').slice(-10);
  const q =
    channel.value === 'whatsapp'
      ? normalizeDoDigits(raw)
      : raw.replace(/^@/, '').toLowerCase();
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${props.accountId}/contacts/search?q=${encodeURIComponent(q)}&include=contact_inboxes`
    );
    const results = data.payload || [];
    if (channel.value === 'whatsapp') {
      return (
        results.find(c => last10(c.phone_number) === last10(q)) ||
        (results.length === 1 ? results[0] : null)
      );
    }
    return (
      results.find(c => String(c.identifier || '').toLowerCase() === `instagram:${q}`) ||
      results.find(c => String(c.name || '').toLowerCase() === q) ||
      null
    );
  } catch (_) {
    return null;
  }
}

// Pre-chequeo: si ya existe, guarda el contacto (para ofrecer "escribir directo")
// y aborta la creación (Victor 2026-05-16).
async function assertNotDuplicate() {
  const dup = await locateDuplicate();
  if (dup) {
    dupContact.value = dup;
    throw new Error('ya existe');
  }
}

async function createContact() {
  const idRaw = newIdentifier.value.trim();
  const payload = { name: newName.value.trim(), inbox_id: inboxId.value };
  if (channel.value === 'whatsapp') {
    const digits = normalizeDoDigits(idRaw);
    if (digits.length < 8) throw new Error('Teléfono inválido');
    await assertNotDuplicate();
    payload.phone_number = `+${digits}`;
    payload.identifier = `whatsapp:${digits}`;
    payload.source_id = `${digits}@c.us`;
  } else {
    const handle = idRaw.replace(/^@/, '');
    await assertNotDuplicate();
    payload.identifier = `instagram:${handle}`;
    payload.source_id = handle;
  }
  const { data } = await axios.post(
    `/api/v1/accounts/${props.accountId}/contacts`,
    payload
  );
  return data.payload?.contact?.id || data.payload?.id || data.id;
}

async function save(openChat) {
  if (!canSave.value || saving.value) return;
  saving.value = true;
  errorMsg.value = null;
  dupContact.value = null;
  try {
    let contactId;
    let contactName;
    if (selectedContact.value) {
      contactId = selectedContact.value.id;
      contactName = selectedContact.value.name;
    } else {
      contactId = await createContact();
      contactName = newName.value.trim();
    }
    const { data } = await DealsAPI.create({
      deal: {
        pipeline_id: props.pipeline.id,
        pipeline_stage_id: stageId.value,
        contact_id: contactId,
        title: title.value || contactName,
        value: value.value || 0,
        currency: currency.value,
      },
    });
    // La respuesta de crear el deal NO trae el teléfono ni el branch del
    // contacto · los adjuntamos para que "Crear y abrir chat" abra (o cree) la
    // conversación en la bandeja de la sucursal directo, sin caer en la ficha
    // del contacto (Victor 2026-05-22).
    const phoneE164 = selectedContact.value
      ? selectedContact.value.phone_number
      : channel.value === 'whatsapp'
        ? `+${normalizeDoDigits(newIdentifier.value)}`
        : null;
    const chosenInbox = inboxes.value.find(i => i.id === inboxId.value);

    // "Crear y abrir chat": creamos la conversación AQUÍ mismo en la bandeja
    // elegida y la abrimos directo (no pasamos por la ficha del contacto). Si
    // la conversación ya existe, no es fatal → el padre abre la existente.
    let conversationId = null;
    if (openChat && channel.value === 'whatsapp') {
      const waDigits = selectedContact.value
        ? String(selectedContact.value.phone_number || '').replace(/[^0-9]/g, '')
        : normalizeDoDigits(newIdentifier.value);
      if (inboxId.value && waDigits.length >= 8) {
        try {
          const conv = await store.dispatch('contactConversations/create', {
            isFromWhatsApp: true,
            params: {
              inboxId: inboxId.value,
              contactId,
              sourceId: `${waDigits}@c.us`,
            },
          });
          conversationId = conv?.id || conv?.payload?.id || null;
        } catch (convErr) {
          // ya existe / no se pudo · el padre buscará/creará como fallback
          conversationId = null;
        }
      }
    }

    const dealForEvent = {
      ...data,
      contact_id: contactId,
      branch_id: data.branch_id || chosenInbox?.branch_id || null,
      conversation_id: data.conversation_id || conversationId,
      // Inbox elegido en el modal · para abrir/crear la conversación en ESA
      // bandeja directo (no depende de adivinar la conectada · funciona en lab).
      _newLeadInboxId: inboxId.value,
      contact: {
        ...(data.contact || {}),
        id: contactId,
        name: contactName,
        phone_number: phoneE164 || data.contact?.phone_number,
      },
    };
    emit('created', dealForEvent, { openChat: !!openChat });
  } catch (e) {
    const raw =
      e.response?.data?.message || e.response?.data?.error || e.message || '';
    if (/has already been taken|already.*taken|ya existe/i.test(raw)) {
      // Duplicado: localiza el contacto existente para ofrecer "escribir directo".
      if (!dupContact.value) dupContact.value = await locateDuplicate();
      errorMsg.value = dupContact.value
        ? null
        : 'Ese número/usuario ya existe. Elígelo de la lista en vez de crear uno nuevo.';
    } else {
      errorMsg.value = raw;
    }
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div
    class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    @click.self="emit('close')"
  >
    <div
      class="w-full max-w-md rounded-[22px] bg-[#131219] border border-white/10 shadow-[0_40px_90px_rgba(0,0,0,0.6)] p-6 max-h-[88vh] overflow-y-auto"
    >
      <!-- Header -->
      <div class="flex items-center justify-between">
        <h3 class="text-xl font-extrabold text-white tracking-tight">Nuevo lead</h3>
        <button
          @click="emit('close')"
          class="w-8 h-8 rounded-full bg-white/5 border border-white/10 text-white/60 hover:text-white text-lg leading-none flex items-center justify-center"
        >
          ×
        </button>
      </div>

      <!-- Etapa (pill editable, prellenada con la columna) -->
      <div
        class="relative inline-flex items-center gap-1.5 mt-3 mb-5 pl-3 pr-6 py-1.5 rounded-lg bg-amber-400/10 border border-amber-400/30"
        :title="currentStageName"
      >
        <span class="text-amber-400 text-xs font-extrabold">→</span>
        <select
          v-model="stageId"
          class="appearance-none bg-transparent text-amber-400 text-xs font-extrabold tracking-wide uppercase focus:outline-none cursor-pointer"
        >
          <option
            v-for="s in pipeline.stages"
            :key="s.id"
            :value="s.id"
            class="bg-slate-900 text-white normal-case"
          >
            {{ s.name }}
          </option>
        </select>
        <span class="absolute right-2 text-amber-400/70 text-[9px] pointer-events-none">▾</span>
      </div>

      <!-- Campo único: Cliente -->
      <label class="text-[10px] font-extrabold uppercase tracking-wider text-white/40 block mb-2">Cliente</label>

      <!-- Estado A: buscando / tecleando -->
      <div v-if="!selectedContact && !creatingNew">
        <div
          class="flex items-center gap-2.5 rounded-xl px-3.5 py-3 bg-white/5 border-[1.5px] border-amber-400/30 ring-2 ring-amber-400/10"
        >
          <span class="text-amber-400 text-sm">🔎</span>
          <input
            v-model="query"
            type="text"
            placeholder="Nombre o teléfono de WhatsApp…"
            class="flex-1 bg-transparent outline-none text-[14.5px] font-semibold text-white placeholder:text-white/35 placeholder:font-medium"
          />
        </div>

        <div
          v-if="showResults"
          class="mt-2 rounded-xl bg-[#08080b] border border-white/10 overflow-hidden"
        >
          <button
            v-for="c in searchResults"
            :key="c.id"
            @click="pickContact(c)"
            class="w-full text-left flex items-center gap-3 px-3.5 py-2.5 border-b border-white/5 hover:bg-amber-400/5"
          >
            <img v-if="c.thumbnail" :src="c.thumbnail" class="w-8 h-8 rounded-[9px] object-cover" />
            <div
              v-else
              class="w-8 h-8 rounded-[9px] bg-gradient-to-br from-slate-600 to-slate-700 text-slate-200 text-xs font-extrabold flex items-center justify-center"
            >
              {{ (c.name || '?').charAt(0).toUpperCase() }}
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-[13px] font-extrabold text-white truncate">{{ c.name }}</div>
              <div class="text-[11px] text-white/45 font-semibold truncate">
                {{ c.phone_number || c.email || '—' }}
              </div>
            </div>
            <span class="text-white/30 text-[13px]">›</span>
          </button>

          <div v-if="searching" class="px-3.5 py-2 text-[11px] text-white/45 font-semibold">Buscando…</div>

          <button
            @click="startCreateNew"
            class="w-full text-left flex items-center gap-3 px-3.5 py-2.5 hover:bg-green-500/[0.06]"
          >
            <div
              class="w-8 h-8 rounded-[9px] bg-green-500/10 border border-dashed border-green-500/50 text-green-500 text-base flex items-center justify-center"
            >
              +
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-[13px] font-extrabold text-green-500 truncate">
                Crear nuevo · «{{ query.trim() }}»
              </div>
              <div class="text-[11px] text-white/45 font-semibold">WhatsApp · contacto nuevo</div>
            </div>
            <span class="text-white/30 text-[13px]">›</span>
          </button>
        </div>
      </div>

      <!-- Estado B: contacto existente elegido -->
      <div
        v-else-if="selectedContact"
        class="flex items-center gap-2.5 rounded-xl px-3.5 py-3 bg-white/5 border-[1.5px] border-white/10"
      >
        <span class="text-green-500 text-sm">✓</span>
        <img v-if="selectedContact.thumbnail" :src="selectedContact.thumbnail" class="w-7 h-7 rounded-full object-cover" />
        <div class="flex-1 min-w-0">
          <div class="text-[13.5px] font-extrabold text-white truncate">{{ selectedContact.name }}</div>
          <div class="text-[11px] text-white/45 font-semibold truncate">{{ selectedContact.phone_number || '—' }}</div>
        </div>
        <button @click="reset" class="text-white/40 hover:text-red-400 text-base">×</button>
      </div>

      <!-- Estado C: crear contacto nuevo -->
      <div v-else class="space-y-3">
        <div class="flex items-center justify-between">
          <div class="flex gap-1 bg-white/5 rounded-lg p-1">
            <button
              class="px-3 py-1.5 text-[11px] font-extrabold rounded-md flex items-center gap-1.5"
              :class="channel === 'whatsapp' ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'text-white/45'"
              @click="channel = 'whatsapp'"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-green-400" /> WhatsApp
            </button>
            <button
              class="px-3 py-1.5 text-[11px] font-extrabold rounded-md flex items-center gap-1.5"
              :class="channel === 'instagram' ? 'bg-pink-500/15 text-pink-400 border border-pink-500/30' : 'text-white/45'"
              @click="channel = 'instagram'"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-pink-400" /> Instagram
            </button>
          </div>
          <button @click="reset" class="text-white/40 hover:text-red-400 text-xs font-bold">← volver</button>
        </div>
        <input
          v-model="newName"
          type="text"
          placeholder="Nombre del cliente"
          class="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-3 text-sm font-semibold text-white placeholder:text-white/35 outline-none focus:border-amber-400/40"
        />
        <!-- WhatsApp: prefijo fijo +1 (Rep. Dom.) · solo escribes 809/849/829 + número -->
        <div
          v-if="channel === 'whatsapp'"
          class="flex items-stretch w-full bg-white/5 border border-white/10 rounded-xl overflow-hidden focus-within:border-amber-400/40"
        >
          <span
            class="flex items-center px-3.5 text-sm font-extrabold text-amber-300 bg-white/5 border-r border-white/10 select-none"
          >
            🇩🇴 +1
          </span>
          <input
            v-model="newIdentifier"
            type="tel"
            inputmode="numeric"
            placeholder="809 000 0000"
            class="flex-1 min-w-0 bg-transparent px-3.5 py-3 text-sm font-semibold text-white placeholder:text-white/35 outline-none"
          />
        </div>
        <input
          v-else
          v-model="newIdentifier"
          type="text"
          placeholder="@usuario"
          class="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-3 text-sm font-semibold text-white placeholder:text-white/35 outline-none focus:border-amber-400/40"
        />
      </div>

      <!-- Bandeja (auto · solo al crear nuevo) -->
      <div v-if="creatingNew" class="mt-3">
        <div
          v-if="channelInboxes.length"
          class="flex items-center gap-1.5 ml-0.5 text-[11.5px] text-white/50 font-semibold flex-wrap"
        >
          <span class="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_6px_#22c55e]" />
          Por
          <b class="text-white/80 font-extrabold">{{ selectedInbox?.name || channelInboxes[0]?.name }}</b>
          <button
            @click="showInboxPicker = !showInboxPicker"
            class="text-amber-400 font-extrabold ml-1"
          >
            cambiar
          </button>
        </div>
        <div
          v-else
          class="flex items-center gap-1.5 ml-0.5 text-[11.5px] text-amber-300 font-semibold"
        >
          ⚠ No hay bandeja de {{ channel === 'whatsapp' ? 'WhatsApp' : 'Instagram' }} en tu sucursal
        </div>
        <select
          v-if="showInboxPicker && channelInboxes.length"
          v-model="inboxId"
          class="mt-2 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white outline-none"
        >
          <option v-for="i in channelInboxes" :key="i.id" :value="i.id" class="bg-slate-900">{{ i.name }}</option>
        </select>
      </div>

      <!-- Más detalles (plegado) -->
      <div class="mt-4 border-t border-white/[0.06] pt-1">
        <button
          @click="showMore = !showMore"
          class="flex items-center gap-1.5 py-2 text-[11px] font-extrabold uppercase tracking-wide"
          :class="showMore ? 'text-amber-400' : 'text-white/45'"
        >
          Más detalles <span class="text-[9px]">{{ showMore ? '▴' : '▾' }}</span>
        </button>
        <div v-if="showMore" class="space-y-3 pt-1">
          <input
            v-model="title"
            placeholder="Título (opcional) · Ej: Vestido de quinceañera"
            class="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-3 text-sm font-semibold text-white placeholder:text-white/35 outline-none focus:border-amber-400/40"
          />
          <div class="grid grid-cols-2 gap-2.5">
            <input
              v-model.number="value"
              type="number"
              min="0"
              placeholder="Valor"
              class="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-3 text-sm font-semibold text-white outline-none focus:border-amber-400/40"
            />
            <select
              v-model="currency"
              class="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-sm text-white outline-none"
            >
              <option value="DOP" class="bg-slate-900">DOP</option>
              <option value="USD" class="bg-slate-900">USD</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Duplicado detectado: en vez de bloquear, ofrece escribirle directo -->
      <div
        v-if="dupContact"
        class="mt-3 rounded-xl bg-amber-400/[0.07] border border-amber-400/25 p-3"
      >
        <div class="flex items-center gap-2.5">
          <img
            v-if="dupContact.thumbnail"
            :src="dupContact.thumbnail"
            class="w-9 h-9 rounded-[10px] object-cover"
          />
          <div
            v-else
            class="w-9 h-9 rounded-[10px] bg-gradient-to-br from-amber-500/30 to-amber-600/20 text-amber-200 text-xs font-extrabold flex items-center justify-center"
          >
            {{ (dupContact.name || '?').charAt(0).toUpperCase() }}
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-[13px] font-extrabold text-white truncate">
              {{ dupContact.name || 'Contacto' }}
            </div>
            <div class="text-[11px] text-amber-200/70 font-semibold truncate">
              Este cliente ya existe · {{ dupContact.phone_number || '—' }}
            </div>
          </div>
        </div>
        <button
          @click="writeToExisting"
          :disabled="saving"
          class="w-full mt-3 px-4 py-2.5 rounded-xl text-[13px] font-extrabold bg-gradient-to-br from-green-500 to-green-600 text-[#04210f] shadow-[0_0_14px_rgba(34,197,94,0.35)] flex items-center justify-center gap-2 disabled:opacity-40"
        >
          💬 {{ saving ? 'Abriendo…' : `Escribir directo a ${dupContact.name || 'este cliente'}` }}
        </button>
      </div>

      <div
        v-else-if="errorMsg"
        class="mt-3 text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-2.5"
      >
        {{ errorMsg }}
      </div>

      <!-- Acciones -->
      <div class="flex gap-2.5 mt-5">
        <button
          @click="save(false)"
          :disabled="saving || !canSave"
          class="px-4 py-3 rounded-xl text-[13.5px] font-extrabold bg-white/6 border border-white/10 text-white disabled:opacity-40"
        >
          Solo crear
        </button>
        <button
          @click="save(true)"
          :disabled="saving || !canSave"
          class="flex-1 px-4 py-3 rounded-xl text-[13.5px] font-extrabold bg-gradient-to-br from-green-500 to-green-600 text-[#04210f] shadow-[0_0_16px_rgba(34,197,94,0.4)] disabled:opacity-40 flex items-center justify-center gap-2"
        >
          💬 {{ saving ? 'Creando…' : 'Crear y abrir chat' }}
        </button>
      </div>
    </div>
  </div>
</template>
